import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { OrdersWithoutWeightApiAdapter } from '../adapters/OrdersWithoutWeightApiAdapter';
import { Filters } from '../models/Filters';
import { Service } from '../services/Service';

export const useOrdersWithoutWeight = (filters: Filters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new OrdersWithoutWeightApiAdapter(httpClient);
  const service = new Service(apiAdapter);

  // 🎯 Serializar filtros de forma estable para la queryKey
  const stableFiltersKey = useMemo(() => {
    const normalizeValue = (value: any): any => {
      if (value === null || value === undefined) {
        return undefined;
      }

      if (Array.isArray(value)) {
        if (value.length === 0) {
          return undefined;
        }
        return value.map(normalizeValue).filter((v) => v !== undefined);
      }

      if (typeof value === 'object') {
        const keys = Object.keys(value).sort();
        if (keys.length === 0) {
          return undefined;
        }
        const normalized: Record<string, any> = {};
        keys.forEach((key) => {
          const normalizedValue = normalizeValue(value[key]);
          if (normalizedValue !== undefined) {
            normalized[key] = normalizedValue;
          }
        });
        return Object.keys(normalized).length > 0 ? normalized : undefined;
      }

      return value;
    };

    const normalizedFilters = normalizeValue(filters);

    if (!normalizedFilters || Object.keys(normalizedFilters).length === 0) {
      return '';
    }

    return JSON.stringify(normalizedFilters);
  }, [filters]);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useInfiniteQuery({
    queryKey: ['orders-without-weight', stableFiltersKey],
    queryFn: ({ pageParam = 1 }) =>
      service.getPaginated({
        ...filters,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => {
      if (previousData && previousData.pages && previousData.pages.length > 0) {
        const hasValidData = previousData.pages.some(
          (page) => page?.orders && page.orders.length > 0
        );
        return hasValidData ? previousData : undefined;
      }
      return undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  const orders =
    data?.pages.flatMap((page) => page?.orders ?? []) ?? [];

  const totalFromApi = data?.pages?.[0]?.total ?? 0;

  return {
    orders,
    isLoading: isLoading || isPending,
    isFetching,
    error: error ? (error as Error).message : null,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    totalPages: data?.pages.length ?? 0,
    total: totalFromApi,
  };
};

